import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useDashboard } from './DashboardContext';
import { supabase } from '../services/supabaseClient';

export type AdminRole = 'tc_admin' | 'adm_company' | null;

interface AdminContextType {
  adminRole: AdminRole;
  loading: boolean;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { enterprises, enterpriseId } = useDashboard();
  const [adminRole, setAdminRole] = useState<AdminRole>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user.loggedIn || !user.email) {
      setAdminRole(null);
      setLoading(false);
      return;
    }

    // Check if user has superadmin or admin role
    if (user.role === 'superadmin' || user.role === 'admin') {
      setAdminRole('tc_admin');
      setLoading(false);
      return;
    }

    // If role not yet loaded (undefined), check tc_admin emails as fallback
    if (user.role === undefined) {
      const tcAdminEmails = String(import.meta.env.REACT_APP_TC_ADMIN_EMAILS || '')
        .split(',')
        .map((e: string) => e.trim().toLowerCase())
        .filter(Boolean);

      if (user.email && tcAdminEmails.includes(user.email.toLowerCase())) {
        setAdminRole('tc_admin');
        setLoading(false);
        return;
      }

      setLoading(true);
      return;
    }

    const email = user.email.toLowerCase();

    // Check tc_admin — list of emails from env var
    const tcAdminEmails = String(import.meta.env.REACT_APP_TC_ADMIN_EMAILS || '')
      .split(',')
      .map((e: string) => e.trim().toLowerCase())
      .filter(Boolean);

    if (tcAdminEmails.includes(email)) {
      setAdminRole('tc_admin');
      setLoading(false);
      return;
    }

    // Check adm_company — query enterprises table for matching adm_company email
    const ids = enterprises.length > 0
      ? enterprises.map(e => e.id)
      : enterpriseId
        ? [enterpriseId]
        : [];

    if (ids.length === 0) {
      setAdminRole(null);
      setLoading(false);
      return;
    }

    Promise.resolve(
      supabase
        .schema('api').from('enterprises')
        .select('adm_company')
        .in('id', ids)
    ).then(({ data }) => {
      if (data && data.some(row => (row.adm_company || '').toLowerCase() === email)) {
        setAdminRole('adm_company');
      } else {
        setAdminRole(null);
      }
      setLoading(false);
    }).catch(() => {
      setAdminRole(null);
      setLoading(false);
    });
  }, [user.loggedIn, user.email, user.role, enterprises, enterpriseId]);

  return (
    <AdminContext.Provider value={{ adminRole, loading }}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) throw new Error('useAdmin must be used within an AdminProvider');
  return context;
};
